import { noop } from 'lodash';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { INITIAL_PAGE } from '../../../../../constants/table';
import { INITIAL_PAGE_SIZE_MODAL } from '../../../../../constants/templates';
import { IClaimLinkTemplate, ITemplateCardConfig } from '../../../../../types/templates';
import { IPaginatedListRef } from '../../../../PaginatedList/PaginatedList';
import { ClaimLinkTemplatesList } from '../../../../TemplatesComponent';
import { useFilters } from '../Filters/useFilters';
import { ModalBodyShell } from './ModalBodyShell';
import { ModalFooter } from './ModalFooter';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  hasSelection: boolean;
  cardConfig: ITemplateCardConfig;
  departmentId?: string;
  orgId?: string;
  withFooterActions?: boolean;
}

export const PaginatedTemplatesBody: FC<IProps> = ({
  isOpen,
  onClose,
  onSubmit,
  hasSelection,
  cardConfig,
  departmentId,
  orgId,
  withFooterActions = false,
}) => {
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const paginatedListRef = useRef<IPaginatedListRef<IClaimLinkTemplate>>(null);

  const {
    Filters,
    claimLinkTemplates,
    isTemplatesFetching,
    filtersRef,
    templateTotalCount,
    fetchClaimLinkTemplates,
    filters: { query: filtersQuery, selectedDepartments: filtersSelectedDepartments },
    onClear,
    isSubmitDisabled,
  } = useFilters({
    onSubmit,
    hasSelection,
    departmentId,
    orgId,
    showActionButton: !withFooterActions,
  });

  const handlePaginationChange = useCallback(
    ({ page, setPage }: { page: number; setPage: (p: number) => void }) => {
      fetchClaimLinkTemplates({
        page,
        page_size: INITIAL_PAGE_SIZE_MODAL,
      });

      setPage(page);
      setCurrentPage(page);
    },
    [fetchClaimLinkTemplates],
  );

  const hasFiltersApplied = Boolean(filtersQuery || filtersSelectedDepartments?.length);

  const fetchClaimLinkTemplatesRef = useRef(fetchClaimLinkTemplates);
  fetchClaimLinkTemplatesRef.current = fetchClaimLinkTemplates;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCurrentPage(INITIAL_PAGE);
    onClear();
    fetchClaimLinkTemplatesRef.current({ page: INITIAL_PAGE, page_size: INITIAL_PAGE_SIZE_MODAL });
  }, [isOpen, onClear]);

  const isFirstOpen = useRef(true);

  useEffect(() => {
    if (!isOpen) {
      isFirstOpen.current = true;
      return;
    }
    // Skip the first render after open — the open-effect above already fetched.
    if (isFirstOpen.current) {
      isFirstOpen.current = false;
      return;
    }
    fetchClaimLinkTemplatesRef.current({ page: INITIAL_PAGE, page_size: INITIAL_PAGE_SIZE_MODAL });
    setCurrentPage(INITIAL_PAGE);
  }, [isOpen, filtersQuery, filtersSelectedDepartments]);

  return (
    <React.Fragment>
      <ModalBodyShell filters={Filters}>
        <ClaimLinkTemplatesList
          ref={paginatedListRef}
          templates={claimLinkTemplates}
          isLoading={isTemplatesFetching}
          filtersRef={filtersRef}
          totalCount={templateTotalCount}
          pageSize={INITIAL_PAGE_SIZE_MODAL}
          page={currentPage}
          onChange={handlePaginationChange}
          onPageSizeChange={noop}
          cardConfig={cardConfig}
          hasFiltersApplied={hasFiltersApplied}
          disableItemsPerPage
          isModal
        />
      </ModalBodyShell>
      {withFooterActions && <ModalFooter onCancel={onClose} onDone={onSubmit} isDoneDisabled={isSubmitDisabled} />}
    </React.Fragment>
  );
};
